/**
 * LiquidGlassButton — premium liquid glass button
 * Glass layers (bottom → top):
 *   1. Deep backdrop blur + saturation
 *   2. Base dark tint
 *   3. Active color radial fill (when isActive)
 *   4. Top-left specular — "curved glass catching light"
 *   5. Rim caustic (bottom-right counter-reflection)
 *   6. Inset gradient-border via layered box-shadows
 *   7. SVG feTurbulence noise overlay (liquid micro-texture)
 */
import React from 'react';
import { Box, BoxProps } from '@chakra-ui/react';

let filterInjected = false;
function ensureFilter() {
  if (filterInjected || typeof document === 'undefined') return;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('style', 'position:absolute;width:0;height:0;overflow:hidden');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = `
    <defs>
      <filter id="lgb-noise" x="0%" y="0%" width="100%" height="100%" color-interpolation-filters="sRGB">
        <feTurbulence type="fractalNoise" baseFrequency="0.68 0.72" numOctaves="4" seed="6" result="noise"/>
        <feColorMatrix type="saturate" values="0" in="noise" result="grey"/>
        <feBlend in="SourceGraphic" in2="grey" mode="soft-light" result="noisy"/>
        <feComposite in="noisy" in2="SourceGraphic" operator="in"/>
      </filter>
    </defs>`;
  document.body.appendChild(svg);
  filterInjected = true;
}

// Parse hex to rgb tuple
function hexRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

export interface LiquidGlassButtonProps extends BoxProps {
  isActive?: boolean;
  activeColor?: string;
  shape?: 'circle' | 'pill' | 'rect';
  circleSize?: number | string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const LiquidGlassButton = React.forwardRef<HTMLElement, LiquidGlassButtonProps>(({
  isActive = false,
  activeColor,
  shape = 'rect',
  circleSize = 60,
  children,
  onClick,
  ...rest
}, ref) => {
  React.useEffect(() => { ensureFilter(); }, []);

  const borderRadius =
    shape === 'circle' ? '50%' :
    shape === 'pill'   ? '999px' :
    '16px';

  const sizeProps: BoxProps =
    shape === 'circle'
      ? { w: circleSize, h: circleSize, minW: circleSize, minH: circleSize }
      : shape === 'pill'
      ? { minH: '46px', px: 5 }
      : { minH: '50px', px: 4 };

  const buildBackground = (): string => {
    // Specular: bright ellipse from top-left simulating curved glass
    const specular = `radial-gradient(ellipse at 28% 16%, rgba(255,255,255,0.48) 0%, rgba(255,255,255,0.14) 34%, transparent 56%)`;
    // Caustic: dim counter-reflection from bottom-right
    const caustic  = `radial-gradient(ellipse at 78% 90%, rgba(255,255,255,0.06) 0%, transparent 48%)`;
    // Rim light: subtle right-edge reflection
    const rim      = `radial-gradient(ellipse at 96% 40%, rgba(255,255,255,0.05) 0%, transparent 35%)`;
    const baseTint = `rgba(4,7,18,0.62)`;

    if (isActive && activeColor) {
      const [r, g, b] = hexRgb(activeColor);
      const fill   = `radial-gradient(ellipse at 50% 58%, rgba(${r},${g},${b},0.42) 0%, rgba(${r},${g},${b},0.14) 55%, transparent 78%)`;
      const topFill = `radial-gradient(ellipse at 40% 0%, rgba(${r},${g},${b},0.18) 0%, transparent 50%)`;
      const specA  = `radial-gradient(ellipse at 28% 16%, rgba(255,255,255,0.42) 0%, rgba(255,255,255,0.1) 32%, transparent 54%)`;
      return [specA, caustic, rim, fill, topFill, `rgba(4,7,18,0.52)`, `rgba(${r},${g},${b},0.12)`].join(', ');
    }

    return [specular, caustic, rim, baseTint].join(', ');
  };

  const buildShadow = (): string => {
    const layers = [
      // Outer depth
      '0 6px 28px rgba(0,0,0,0.42)',
      '0 2px 8px rgba(0,0,0,0.28)',
      // Simulated gradient border via inset shadows
      'inset 0 0 0 1px rgba(255,255,255,0.13)',   // base perimeter
      'inset 0 1.5px 0 rgba(255,255,255,0.44)',   // top bright edge (glass catching light)
      'inset 0 -1.5px 0 rgba(0,0,0,0.36)',         // bottom dark edge (glass depth)
      'inset 1.5px 0 rgba(255,255,255,0.1)',        // left edge
      'inset -1.5px 0 rgba(0,0,0,0.14)',            // right edge
    ];

    if (isActive && activeColor) {
      const [r, g, b] = hexRgb(activeColor);
      layers.unshift(
        `0 0 28px rgba(${r},${g},${b},0.55)`,
        `0 0 56px rgba(${r},${g},${b},0.22)`,
      );
      // Strengthen top edge when active
      layers[layers.length - 3] = 'inset 0 1.5px 0 rgba(255,255,255,0.5)';
    }

    return layers.join(', ');
  };

  return (
    <Box
      as="button"
      ref={ref}
      role="button"
      position="relative"
      display="flex"
      alignItems="center"
      justifyContent="center"
      borderRadius={borderRadius}
      {...sizeProps}
      cursor="pointer"
      outline="none"
      userSelect="none"
      _focus={{ outline: 'none', boxShadow: buildShadow() }}
      _focusVisible={{ outline: 'none', boxShadow: buildShadow() }}
      // Glass substrate
      backdropFilter="blur(16px) saturate(180%)"
      background={buildBackground()}
      border="none"
      boxShadow={buildShadow()}
      // Transitions
      transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
      transform={isActive ? 'scale(1.05)' : 'scale(1)'}
      _hover={{
        transform: isActive ? 'scale(1.07)' : 'scale(1.03)',
        boxShadow: buildShadow(),
      }}
      _active={{
        transform: 'scale(0.95)',
        transition: 'transform 0.1s ease',
      }}
      onClick={onClick}
      sx={{
        WebkitBackdropFilter: 'blur(16px) saturate(180%)',
        ...((rest as any).sx || {}),
      }}
      {...rest}
    >
      {/* Liquid noise micro-texture overlay */}
      <Box
        position="absolute" inset={0} borderRadius="inherit"
        pointerEvents="none" opacity={0.055} zIndex={0}
        sx={{ filter: 'url(#lgb-noise)', mixBlendMode: 'overlay' }}
        bg="white"
      />
      {/* Content layer */}
      <Box
        position="relative" zIndex={1}
        display="flex" alignItems="center" justifyContent="center"
        w="100%" h="100%"
      >
        {children}
      </Box>
    </Box>
  );
});

LiquidGlassButton.displayName = 'LiquidGlassButton';
export default LiquidGlassButton;
